"use client";

import { useState, Suspense } from "react";
import { useRouter, useSearchParams } from "next/navigation";

function LoginForm() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  // Get error or message from URL params
  const urlError = searchParams.get("error");
  const urlMessage = searchParams.get("message");

  const getErrorMessage = (error: string) => {
    switch (error) {
      case "invalid-token":
        return "Invalid login link. Please request a new one.";
      case "token-expired":
        return "This login link has expired. Please request a new one.";
      case "invalid-invitation":
        return "Invalid invitation link. Please contact your workshop administrator.";
      default:
        return "An error occurred. Please try again.";
    }
  };

  const getMessage = (message: string) => {
    switch (message) {
      case "invitation-accepted":
        return "Invitation accepted! Please enter your email to log in.";
      default:
        return "";
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setMessage("");
    setError("");

    console.log("Submitting login for email:", email);

    try {
      const response = await fetch("/api/auth/send-magic-link", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });

      console.log("Response status:", response.status);
      const data = await response.json();
      console.log("Response data:", data);

      if (response.ok) {
        setMessage("Check your email! We've sent you a login link.");
        setEmail("");
      } else {
        // Use the specific error message from the API, which includes "No account found with this email"
        console.error("API error:", data.error);
        setError(data.error || "Failed to send login link");
      }
    } catch (err) {
      console.error("Caught error:", err);
      setError("An error occurred. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="w-full max-w-md mx-auto">
      <div className="text-center mb-8">
        {/* Logo */}
        <div className="mb-8 flex justify-center pt-6">
          <svg viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg" className="w-12 h-12">
            <path d="M6.16,15.89h-2.91v-2.78c0-1.48.82-1.56,2.17-1.69.08,0,.15-.01.22-.02v-.96c0-1.39.76-1.49,1.91-1.65.08-.01.17-.02.26-.04,1.19-.17,2.14-.39,2.86-.63-.65-.21-1.52-.43-2.71-.64-3.31-.58-4.92-1.34-4.92-2.33,0-.46.35-.86.96-1.21-.64-.35-.96-.75-.96-1.19C3.04,1.04,8.02.11,12.7.11h.26v2.91h-.26c-2.49,0-4.83.25-6.59.69-.29.07-.55.15-.79.23.65.21,1.52.43,2.72.64,3.31.58,4.92,1.34,4.92,2.33,0,.49-.39.89-.96,1.21.64.35.96.75.96,1.19,0,.84-1.2,1.96-6.81,2.56v4.02ZM3.76,15.37h1.87v-3.45c-.05,0-.11.01-.17.02-1.41.13-1.71.2-1.71,1.17v2.26ZM11.41,8.41c-1.15.47-2.67.74-3.53.86-.09.01-.18.02-.26.04-1.15.16-1.46.2-1.46,1.14v.9c4.63-.51,6.29-1.4,6.29-2.04,0-.14-.11-.49-1.04-.9ZM4.59,4.23c-.74.34-1.03.68-1.03.93s.32,1.09,4.49,1.82c1.44.25,2.56.54,3.36.86.7-.32,1.04-.65,1.04-.93,0-.24-.32-1.09-4.49-1.82-1.44-.25-2.56-.54-3.37-.86ZM12.44.63c-2.39.02-4.63.26-6.33.69-1.89.48-2.56,1.06-2.56,1.44,0,.14.11.49,1.04.9,1.79-.74,4.86-1.14,7.85-1.16V.63Z" fill="#f3392f"/>
          </svg>
        </div>
        <h1 className="mb-2">Login</h1>
        <p>Enter your email to receive a login link</p>
      </div>

      {(urlError || error) && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          {urlError ? getErrorMessage(urlError) : error}
        </div>
      )}

      {(urlMessage || message) && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">
          {urlMessage ? getMessage(urlMessage) : message}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="email" className="block text-sm font-medium mb-2">
            Email address
          </label>
          <input
            type="email"
            id="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#F3392F] focus:border-transparent"
            placeholder="you@example.com"
            required
            disabled={isLoading}
          />
        </div>

        <button
          type="submit"
          disabled={isLoading}
          className="w-full bg-[#F3392F] text-white py-2 px-4 rounded-lg hover:bg-[#F3392F]/90 focus:outline-none focus:ring-2 focus:ring-[#F3392F] focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {isLoading ? "Sending..." : "Send login link"}
        </button>
      </form>
      
      {message && (
        <div className="mt-4 text-xs text-gray-400 text-center">
          Session: {message === "registration-complete" ? "Registration successful! Please log in." : ""}
        </div>
      )}
    </div>
  );
}

export default function LoginPage() {
  return (
    <Suspense fallback={<div className="w-full max-w-md mx-auto text-center">Loading...</div>}>
      <LoginForm />
    </Suspense>
  );
}
